import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import type {
  Hilfsartikel,
  HilfsartikelList,
  KomponentenZeile,
  Kostenart,
  Kundenartikel,
  KundenartikelList,
  Schnellkalkulation,
} from "@bidi-handel/core/schemas";
import { api, ApiError } from "../../api/client";

/**
 * Dialog zum Aufbau einer Schnellkalkulation als Alternative zum
 * 1-Artikel-Match.
 *
 * Aufbau:
 *  - Header: Position-OZ + Originaltext + Mengenbezug-Toggle (pro_einheit /
 *    pauschal). Default haengt an `lvEinheit` — `psch` → pauschal, sonst
 *    pro_einheit.
 *  - Body: Tabelle der Komponentenzeilen mit Quelle, Bezeichnung, Menge,
 *    Einzelpreis, Kostenart-Pill-Picker und Aktionen (Remove, Als
 *    Hilfsartikel speichern bei `quelle="frei"`).
 *  - Footer: 5-Pill-Summenleiste (Lohn/Material/Geraet/Sonstige/Fremd),
 *    Einzelpreis-Total und Gesamtpreis × posMenge.
 *
 * `existing` → vorbelegt; `null` → leere Liste mit einer freien Default-Zeile.
 */

const KOSTENART_LABEL: Record<Kostenart, string> = {
  lohn: "Lohn",
  material: "Material",
  geraet: "Geraet",
  sonstige: "Sonstige",
  fremd: "Fremd",
};

const KOSTENART_ORDER: Kostenart[] = [
  "lohn",
  "material",
  "geraet",
  "sonstige",
  "fremd",
];

const KOSTENART_TONE: Record<Kostenart, string> = {
  lohn: "var(--acc-hot)",
  material: "var(--ok)",
  geraet: "var(--acc)",
  sonstige: "var(--warn)",
  fremd: "var(--fg-dim)",
};

function makeRowId(): string {
  return `z-${Math.random().toString(36).slice(2, 10)}`;
}

function emptyRow(defaultEinheit: string): KomponentenZeile {
  return {
    id: makeRowId(),
    quelle: "frei",
    artikelCode: null,
    bezeichnung: "",
    menge: 1,
    einheit: defaultEinheit || "Stk",
    einzelpreis: 0,
    kostenart: "sonstige",
    preisAuto: false,
  };
}

export function KomponentenDialog({
  tenantId,
  positionId,
  positionOz,
  originaltext,
  lvEinheit,
  lvMenge,
  existing,
  kurzText,
  hauptgruppe,
  provisional,
  onClose,
  onSaved,
}: {
  tenantId: string;
  positionId: string;
  positionOz: string;
  originaltext: string;
  lvEinheit: string;
  lvMenge: number;
  existing: Schnellkalkulation | null;
  kurzText?: string;
  hauptgruppe?: string;
  provisional?: boolean;
  onClose: () => void;
  onSaved: (schnellkalkulation: Schnellkalkulation | null) => Promise<void>;
}) {
  const [kundenartikel, setKundenartikel] = useState<KundenartikelList>([]);
  const [hilfsartikel, setHilfsartikel] = useState<HilfsartikelList>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Default-Mengenbezug aus LV-Einheit: psch → pauschal, sonst pro_einheit.
  const initialMengenbezug: "pro_einheit" | "pauschal" =
    existing?.mengenbezug ??
    (lvEinheit.toLowerCase() === "psch" ? "pauschal" : "pro_einheit");

  const [mengenbezug, setMengenbezug] = useState<"pro_einheit" | "pauschal">(
    initialMengenbezug
  );
  const [notiz, setNotiz] = useState(existing?.notiz ?? "");
  const [zeilen, setZeilen] = useState<KomponentenZeile[]>(() =>
    existing?.zeilen.length ? existing.zeilen : [emptyRow(lvEinheit)]
  );
  const [lvOpen, setLvOpen] = useState(false);
  const [isClosed, setIsClosed] = useState(existing?.closed ?? false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.getKundenartikel(tenantId), api.getHilfsartikel(tenantId)])
      .then(([ka, ha]) => {
        if (!cancelled) {
          setKundenartikel(ka);
          setHilfsartikel(ha);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Summen pro Kostenart + Gesamt.
  const summen = useMemo(() => {
    const acc: Record<Kostenart, number> = {
      lohn: 0,
      material: 0,
      geraet: 0,
      sonstige: 0,
      fremd: 0,
    };
    let total = 0;
    for (const z of zeilen) {
      const zp = round2(z.einzelpreis * z.menge);
      acc[z.kostenart] = round2(acc[z.kostenart] + zp);
      total = round2(total + zp);
    }
    return { perKostenart: acc, total };
  }, [zeilen]);

  const einzelpreis =
    mengenbezug === "pauschal"
      ? lvMenge > 0
        ? round2(summen.total / lvMenge)
        : 0
      : summen.total;
  const gesamtpreis =
    mengenbezug === "pauschal" ? summen.total : round2(einzelpreis * lvMenge);

  function updateZeile(id: string, patch: Partial<KomponentenZeile>) {
    setZeilen((cur) => cur.map((z) => (z.id === id ? { ...z, ...patch } : z)));
  }
  function removeZeile(id: string) {
    setZeilen((cur) => cur.filter((z) => z.id !== id));
  }
  function addZeile() {
    setZeilen((cur) => [...cur, emptyRow(lvEinheit)]);
  }

  function applyStammartikel(id: string, a: Kundenartikel) {
    updateZeile(id, {
      quelle: "stammartikel",
      artikelCode: a.code,
      bezeichnung: a.bezeichnung,
      einheit: a.einheit ?? lvEinheit ?? "Stk",
      einzelpreis: a.listenpreis ?? 0,
      kostenart: "material",
    });
  }
  function applyHilfsartikel(id: string, h: Hilfsartikel) {
    updateZeile(id, {
      quelle: "hilfsartikel",
      artikelCode: h.code,
      bezeichnung: h.bezeichnung,
      einheit: h.einheit,
      einzelpreis: h.defaultPreis,
      kostenart: h.defaultKostenart,
    });
  }
  function setQuelle(id: string, quelle: KomponentenZeile["quelle"]) {
    // Bei Wechsel der Quelle: Code/Bezeichnung leeren damit der User neu
    // auswaehlt; den Rest belassen.
    updateZeile(id, {
      quelle,
      artikelCode: null,
      bezeichnung: quelle === "frei" ? "" : "",
    });
  }

  async function saveHilfsartikel(zeile: KomponentenZeile) {
    // Heuristik fuer Code: bezeichnung + Kostenart → Slug.
    const baseCode = `${zeile.kostenart.toUpperCase()}-${slug(zeile.bezeichnung).slice(0, 20)}`;
    let code = baseCode || `HILFS-${Date.now()}`;
    // Konflikt vermeiden — falls vorhanden, Suffix anhaengen.
    if (hilfsartikel.some((h) => h.code === code)) {
      code = `${code}-${Math.random().toString(36).slice(2, 6)}`;
    }
    const hilfs: Hilfsartikel = {
      code,
      bezeichnung: zeile.bezeichnung,
      einheit: zeile.einheit,
      defaultPreis: zeile.einzelpreis,
      defaultKostenart: zeile.kostenart,
      bemerkung: zeile.bemerkung,
    };
    try {
      const created = await api.createHilfsartikel(tenantId, hilfs);
      setHilfsartikel((cur) => [...cur, created]);
      // Aktive Zeile auf Hilfsartikel-Bindung umstellen — beim naechsten
      // Speichern referenziert sie den frischen Stammeintrag.
      updateZeile(zeile.id, { quelle: "hilfsartikel", artikelCode: created.code });
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { hint?: string; error?: string } | null;
        setSubmitError(body?.hint ?? body?.error ?? err.message);
      } else if (err instanceof Error) {
        setSubmitError(err.message);
      } else {
        setSubmitError(String(err));
      }
    }
  }

  async function submit(action: "save" | "remove" | "close" | "reopen") {
    setSubmitError(null);
    setSubmitting(true);
    try {
      if (action === "remove") {
        await onSaved(null);
        onClose();
        return;
      }
      // Validierung — kein leerer Dialog speichern.
      const validZeilen = zeilen.filter(
        (z) => z.bezeichnung.trim() !== "" && z.menge > 0
      );
      if (validZeilen.length === 0) {
        setSubmitError("Mindestens eine Zeile mit Bezeichnung und Menge > 0 erforderlich.");
        setSubmitting(false);
        return;
      }
      const nextClosed =
        action === "close" ? true : action === "reopen" ? false : isClosed;
      const schnellkalkulation: Schnellkalkulation = {
        zeilen: validZeilen,
        mengenbezug,
        // preisModus/komplettpreis (Set-Stückliste) unverändert durchreichen —
        // die Pflege dieser Felder im Dialog kommt mit der UI-Slice.
        preisModus: existing?.preisModus ?? "summiert",
        komplettpreis: existing?.komplettpreis,
        notiz: notiz.trim() === "" ? undefined : notiz.trim(),
        closed: nextClosed,
      };
      setIsClosed(nextClosed);
      await onSaved(schnellkalkulation);
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { hint?: string; error?: string } | null;
        setSubmitError(body?.hint ?? body?.error ?? err.message);
      } else if (err instanceof Error) {
        setSubmitError(err.message);
      } else {
        setSubmitError(String(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-pair"
        style={{
          display: "flex",
          gap: 16,
          alignItems: "stretch",
          width: "100%",
          maxWidth: lvOpen ? "min(97vw, 2160px)" : 1080,
          justifyContent: "center",
          transition: "max-width 0.32s ease",
        }}
        onClick={(e) => e.stopPropagation()}
      >
      <div
        className="modal"
        style={{
          flex: lvOpen ? "1.6 1 1100px" : "0 0 1080px",
          minWidth: 0,
          maxWidth: lvOpen ? 1480 : undefined,
          transition: "flex-basis 0.32s ease, max-width 0.32s ease",
        }}
        role="dialog"
        aria-label="Schnellkalkulation"
      >
        <div className="modal-head">
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="uppercase-mini">/ MATCH / SCHNELLKALKULATION</div>
            <div className="modal-title" style={{ fontSize: 18 }}>
              {positionOz} · {originaltext}
            </div>
            <div className="uppercase-mini" style={{ marginTop: 8 }}>
              Position: <span className="mono">{positionId}</span> ·{" "}
              <span className="mono">
                {lvMenge} {lvEinheit}
              </span>
            </div>
          </div>
          <button
            type="button"
            className={`btn sm ${lvOpen ? "ghost" : "primary"}`}
            onClick={() => setLvOpen((s) => !s)}
            style={{
              marginRight: 8,
              ...(lvOpen
                ? {
                    borderColor: "var(--acc)",
                    color: "var(--acc)",
                    background: "transparent",
                  }
                : {
                    boxShadow:
                      "0 0 18px oklch(0.78 0.18 200 / 0.55), 0 0 0 1px oklch(0.92 0.08 200 / 0.35) inset",
                  }),
            }}
            aria-pressed={lvOpen}
            title={lvOpen ? "LV-Detail ausblenden" : "LV-Detail einblenden"}
          >
            📋 LV-Detail
          </button>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Schliessen"
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          <div
            style={{
              display: "flex",
              gap: 16,
              alignItems: "center",
              marginBottom: 14,
            }}
          >
            <span className="uppercase-mini">Mengenbezug</span>
            <div role="radiogroup" style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                role="radio"
                aria-checked={mengenbezug === "pro_einheit"}
                className={`btn sm ${mengenbezug === "pro_einheit" ? "primary" : "ghost"}`}
                onClick={() => setMengenbezug("pro_einheit")}
              >
                pro Einheit
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={mengenbezug === "pauschal"}
                className={`btn sm ${mengenbezug === "pauschal" ? "primary" : "ghost"}`}
                onClick={() => setMengenbezug("pauschal")}
              >
                pauschal
              </button>
            </div>
            <span className="dim" style={{ fontSize: 12 }}>
              {mengenbezug === "pro_einheit"
                ? `Komponenten beziehen sich auf 1 ${lvEinheit}. Gesamt = EP × ${lvMenge}.`
                : `Komponenten = voller Auftrag. EP = Gesamt / ${lvMenge}.`}
            </span>
          </div>

          {loadError && (
            <div
              className="warn-line"
              style={{
                borderLeftColor: "var(--bad)",
                background: "oklch(0.72 0.18 25 / 0.08)",
                marginBottom: 14,
              }}
            >
              <strong>Stammdaten nicht ladbar:</strong>{" "}
              <span className="mono">{loadError}</span>
            </div>
          )}

          <div className="komp-table">
            <div className="komp-table-head uppercase-mini">
              <div>Quelle</div>
              <div>Bezeichnung</div>
              <div className="num">Menge</div>
              <div>Einh.</div>
              <div className="num">EP €</div>
              <div>Kostenart</div>
              <div className="num">Σ €</div>
              <div></div>
            </div>
            {zeilen.map((z) => (
              <KompZeile
                key={z.id}
                z={z}
                kundenartikel={kundenartikel}
                hilfsartikel={hilfsartikel}
                onUpdate={(patch) => updateZeile(z.id, patch)}
                onSetQuelle={(q) => setQuelle(z.id, q)}
                onApplyStammartikel={(a) => applyStammartikel(z.id, a)}
                onApplyHilfsartikel={(h) => applyHilfsartikel(z.id, h)}
                onRemove={() => removeZeile(z.id)}
                onSaveAsHilfsartikel={() => saveHilfsartikel(z)}
                disabled={submitting}
              />
            ))}
            <div style={{ padding: "8px 12px" }}>
              <button
                type="button"
                className="btn ghost sm"
                onClick={addZeile}
                disabled={submitting}
              >
                + Zeile hinzufuegen
              </button>
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <label className="uppercase-mini" style={{ display: "block", marginBottom: 6 }}>
              Notiz (optional, geht ins Angebot)
            </label>
            <textarea
              className="input"
              rows={2}
              value={notiz}
              onChange={(e) => setNotiz(e.currentTarget.value)}
              placeholder="z.B. Material liefern und montieren — Geruest gestellt vom AG."
              disabled={submitting}
              style={{ width: "100%", resize: "vertical" }}
            />
          </div>

          <div
            style={{
              marginTop: 16,
              padding: "12px 14px",
              background: "var(--bg-raised)",
              borderRadius: 6,
              border: "1px solid var(--line-soft)",
            }}
          >
            <div className="uppercase-mini" style={{ marginBottom: 8 }}>
              Aufgliederung (EFB 223)
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {KOSTENART_ORDER.map((k) => (
                <span
                  key={k}
                  className="pill"
                  style={{
                    borderColor: KOSTENART_TONE[k],
                    color: KOSTENART_TONE[k],
                    padding: "4px 12px",
                  }}
                >
                  <span style={{ fontWeight: 600, marginRight: 8 }}>
                    {KOSTENART_LABEL[k]}
                  </span>
                  <span className="mono">{fmt(summen.perKostenart[k])}</span>
                </span>
              ))}
            </div>
            <div
              style={{
                marginTop: 12,
                display: "flex",
                gap: 24,
                fontSize: 14,
                alignItems: "baseline",
              }}
            >
              <span>
                EP:{" "}
                <strong className="mono" style={{ color: "var(--acc-hot)" }}>
                  {fmt(einzelpreis)} €
                </strong>
              </span>
              <span>
                Gesamt ({lvMenge} {lvEinheit}):{" "}
                <strong className="mono" style={{ color: "var(--acc-hot)" }}>
                  {fmt(gesamtpreis)} €
                </strong>
              </span>
              <span className="dim" style={{ fontSize: 12 }}>
                Σ Anteile: <span className="mono">{fmt(summen.total)} €</span>
              </span>
            </div>
            {zeilen.some((z) => z.preisAuto) && (
              <div className="dim" style={{ marginTop: 8, fontSize: 11.5 }}>
                ↳ „live"-Komponenten (aus der Stückliste) werden erst im
                Preise-Tab aus dem Artikel bepreist — der hier gezeigte EP
                enthält sie noch nicht.
              </div>
            )}
          </div>

          {submitError && (
            <div
              className="warn-line"
              style={{
                marginTop: 14,
                borderLeftColor: "var(--bad)",
                background: "oklch(0.72 0.18 25 / 0.08)",
              }}
            >
              <strong>Speichern fehlgeschlagen:</strong>{" "}
              <span className="mono">{submitError}</span>
            </div>
          )}
        </div>

        <div className="modal-foot">
          <button
            type="button"
            className="btn ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Abbrechen
          </button>
          <div style={{ flex: 1 }} />
          {existing && (
            <button
              type="button"
              className="btn"
              onClick={() => submit("remove")}
              disabled={submitting}
            >
              Schnellkalk entfernen
            </button>
          )}
          <button
            type="button"
            className="btn"
            onClick={() => submit("save")}
            disabled={submitting}
          >
            {submitting ? "…" : "Speichern"}
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => submit(isClosed ? "reopen" : "close")}
            disabled={submitting}
            style={{
              ...(isClosed
                ? {
                    background: "transparent",
                    borderColor: "var(--ok)",
                    color: "var(--ok)",
                  }
                : {
                    background: "var(--ok)",
                    borderColor: "var(--ok)",
                    color: "oklch(0.12 0 0)",
                    boxShadow: "0 0 18px oklch(0.74 0.18 145 / 0.45)",
                  }),
            }}
          >
            {submitting ? "…" : isClosed ? "Wieder öffnen" : "✓ Abschließen"}
          </button>
        </div>
      </div>
      {lvOpen && (
        <aside
          className="modal lv-detail-panel"
          style={{
            flex: "0.8 1 540px",
            minWidth: 0,
            maxWidth: 720,
            animation: "slide-from-right 0.32s ease both",
          }}
          role="complementary"
          aria-label="LV-Position Detail"
        >
          <div className="modal-head">
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="uppercase-mini">/ LV-POSITION / DETAIL</div>
              <div className="modal-title" style={{ fontSize: 18 }}>
                <span className="mono">{positionOz}</span>
                {kurzText ? <> · {kurzText}</> : null}
              </div>
              {(hauptgruppe || provisional) && (
                <div className="uppercase-mini" style={{ marginTop: 8 }}>
                  {hauptgruppe && (
                    <>
                      Hauptgruppe: <span className="mono">{hauptgruppe}</span>
                    </>
                  )}
                  {hauptgruppe && provisional ? " · " : null}
                  {provisional && (
                    <span style={{ color: "var(--warn)" }}>
                      PROVISORISCH (Mengen aus Plan)
                    </span>
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              className="modal-close"
              onClick={() => setLvOpen(false)}
              aria-label="LV-Detail schliessen"
            >
              ×
            </button>
          </div>
          <div className="modal-body">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 14,
                marginBottom: 18,
              }}
            >
              <div
                style={{
                  padding: "10px 12px",
                  background: "var(--bg-base)",
                  border: "1px solid var(--line-soft)",
                  borderRadius: "var(--r-lg)",
                }}
              >
                <div className="uppercase-mini">OZ</div>
                <div
                  className="mono"
                  style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}
                >
                  {positionOz}
                </div>
              </div>
              <div
                style={{
                  padding: "10px 12px",
                  background: "var(--bg-base)",
                  border: "1px solid var(--line-soft)",
                  borderRadius: "var(--r-lg)",
                }}
              >
                <div className="uppercase-mini">Menge</div>
                <div
                  className="mono"
                  style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}
                >
                  {lvMenge} <span className="dim">{lvEinheit}</span>
                </div>
              </div>
            </div>
            <div className="uppercase-mini" style={{ marginBottom: 6 }}>
              Langtext
            </div>
            <div
              style={{
                padding: "14px 16px",
                background: "var(--bg-base)",
                border: "1px solid var(--line-soft)",
                borderRadius: "var(--r-lg)",
                whiteSpace: "pre-wrap",
                fontSize: 14,
                lineHeight: 1.55,
                color: "var(--fg)",
              }}
            >
              {originaltext}
            </div>
          </div>
        </aside>
      )}
      </div>
    </div>,
    document.body
  );
}

function KompZeile({
  z,
  kundenartikel,
  hilfsartikel,
  onUpdate,
  onSetQuelle,
  onApplyStammartikel,
  onApplyHilfsartikel,
  onRemove,
  onSaveAsHilfsartikel,
  disabled,
}: {
  z: KomponentenZeile;
  kundenartikel: KundenartikelList;
  hilfsartikel: HilfsartikelList;
  onUpdate: (patch: Partial<KomponentenZeile>) => void;
  onSetQuelle: (q: KomponentenZeile["quelle"]) => void;
  onApplyStammartikel: (a: Kundenartikel) => void;
  onApplyHilfsartikel: (h: Hilfsartikel) => void;
  onRemove: () => void;
  onSaveAsHilfsartikel: () => void;
  disabled: boolean;
}) {
  const zeilenpreis = round2(z.einzelpreis * z.menge);
  const showPicker = (z.quelle === "stammartikel" || z.quelle === "hilfsartikel") && !z.artikelCode;
  return (
    <div className="komp-table-row">
      <div>
        <select
          className="input sm"
          value={z.quelle}
          onChange={(e) => onSetQuelle(e.currentTarget.value as KomponentenZeile["quelle"])}
          disabled={disabled}
          aria-label="Quelle der Zeile"
        >
          <option value="frei">frei</option>
          <option value="stammartikel">Stammartikel</option>
          <option value="hilfsartikel">Hilfsartikel</option>
        </select>
      </div>
      <div>
        {showPicker ? (
          z.quelle === "stammartikel" ? (
            <ArtikelCombobox
              items={kundenartikel}
              onPick={(a) => onApplyStammartikel(a)}
              placeholder="Stammartikel suchen …"
              renderLabel={(a) => `${a.code} · ${a.bezeichnung}`}
              renderSearch={(a) =>
                `${a.code} ${a.bezeichnung} ${a.kurzText ?? ""} ${a.warengruppe ?? ""}`.toLowerCase()
              }
              disabled={disabled}
            />
          ) : (
            <ArtikelCombobox
              items={hilfsartikel}
              onPick={(h) => onApplyHilfsartikel(h)}
              placeholder="Hilfsartikel suchen …"
              renderLabel={(h) => `${h.code} · ${h.bezeichnung}`}
              renderSearch={(h) =>
                `${h.code} ${h.bezeichnung} ${h.bemerkung ?? ""}`.toLowerCase()
              }
              disabled={disabled}
            />
          )
        ) : (
          <input
            type="text"
            className="input sm"
            value={z.bezeichnung}
            onChange={(e) => onUpdate({ bezeichnung: e.currentTarget.value })}
            placeholder="Bezeichnung"
            disabled={disabled}
            style={{ width: "100%" }}
          />
        )}
        {z.artikelCode && (
          <div className="dim mono" style={{ fontSize: 11, marginTop: 2 }}>
            Code: {z.artikelCode} ·{" "}
            <button
              type="button"
              className="btn ghost"
              style={{ padding: "0 4px", fontSize: 11 }}
              onClick={() => onUpdate({ artikelCode: null, bezeichnung: "" })}
              disabled={disabled}
            >
              wechseln
            </button>
          </div>
        )}
      </div>
      <div className="num">
        <input
          type="number"
          className="input sm num"
          value={z.menge}
          min={0}
          step="0.01"
          onChange={(e) => onUpdate({ menge: Number(e.currentTarget.value) })}
          disabled={disabled}
          style={{ width: 80 }}
        />
      </div>
      <div>
        <input
          type="text"
          className="input sm"
          value={z.einheit}
          onChange={(e) => onUpdate({ einheit: e.currentTarget.value })}
          disabled={disabled}
          style={{ width: 60 }}
        />
      </div>
      <div className="num">
        {z.preisAuto ? (
          <span
            className="pill acc mono"
            title="Preis wird im Preise-Tab live aus dem verknüpften Artikel berechnet (Listenpreis × Kondition × Marge)."
            style={{ fontSize: 10 }}
          >
            live
          </span>
        ) : (
          <input
            type="number"
            className="input sm num"
            value={z.einzelpreis}
            min={0}
            step="0.01"
            onChange={(e) => onUpdate({ einzelpreis: Number(e.currentTarget.value) })}
            disabled={disabled}
            style={{ width: 100 }}
          />
        )}
      </div>
      <div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 4,
          }}
        >
          {KOSTENART_ORDER.map((k) => {
            const active = z.kostenart === k;
            return (
              <button
                key={k}
                type="button"
                className="btn sm ghost"
                onClick={() => onUpdate({ kostenart: k })}
                disabled={disabled}
                style={{
                  padding: "2px 8px",
                  fontSize: 11,
                  ...(active && {
                    background: `color-mix(in oklch, ${KOSTENART_TONE[k]} 18%, transparent)`,
                    borderColor: KOSTENART_TONE[k],
                    color: KOSTENART_TONE[k],
                  }),
                }}
                aria-pressed={active}
                aria-label={`Kostenart ${KOSTENART_LABEL[k]}`}
              >
                {KOSTENART_LABEL[k]}
              </button>
            );
          })}
        </div>
      </div>
      <div className="num mono">
        {z.preisAuto ? <span className="dim">live</span> : fmt(zeilenpreis)}
      </div>
      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
        {z.quelle === "frei" && z.bezeichnung.trim() !== "" && (
          <button
            type="button"
            className="btn ghost sm"
            onClick={onSaveAsHilfsartikel}
            disabled={disabled}
            title="Als wiederverwendbaren Hilfsartikel speichern"
            style={{ fontSize: 11 }}
          >
            💾
          </button>
        )}
        <button
          type="button"
          className="btn ghost sm"
          onClick={onRemove}
          disabled={disabled}
          aria-label="Zeile entfernen"
          style={{ fontSize: 11 }}
        >
          ×
        </button>
      </div>
    </div>
  );
}

function ArtikelCombobox<T extends { code: string }>({
  items,
  onPick,
  placeholder,
  renderLabel,
  renderSearch,
  disabled,
}: {
  items: T[];
  onPick: (item: T) => void;
  placeholder: string;
  renderLabel: (item: T) => string;
  renderSearch: (item: T) => string;
  disabled: boolean;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const filtered = useMemo(() => {
    if (!query.trim()) return items.slice(0, 20);
    const q = query.toLowerCase();
    return items.filter((i) => renderSearch(i).includes(q)).slice(0, 20);
  }, [items, query, renderSearch]);

  return (
    <div style={{ position: "relative" }}>
      <input
        type="text"
        className="input sm"
        value={query}
        onChange={(e) => {
          setQuery(e.currentTarget.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        disabled={disabled}
        style={{ width: "100%" }}
      />
      {open && filtered.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 10,
            maxHeight: 240,
            overflow: "auto",
            background: "var(--bg-base)",
            border: "1px solid var(--line)",
            borderRadius: 4,
            marginTop: 2,
          }}
        >
          {filtered.map((item) => (
            <button
              key={item.code}
              type="button"
              onClick={() => {
                onPick(item);
                setQuery("");
                setOpen(false);
              }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "6px 10px",
                background: "transparent",
                border: "none",
                color: "var(--fg)",
                cursor: "pointer",
                font: "inherit",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "var(--bg-raised)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              {renderLabel(item)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function fmt(n: number): string {
  return n.toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function slug(s: string): string {
  return s
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
